/**
 * User Service - Phase 4 Architecture Migration
 *
 * Manages user administration, roles, and statistics
 * Migrated to Phase 4 patterns: IService interface, ServiceResponse wrappers, typed Supabase client
 */

import {
  IService,
  ServiceResponse,
  createServiceError,
  createSuccessResponse,
  createErrorResponse,
  ServiceRegistry,
  serviceRegistry,
} from "@/services/core/service-registry";
import { UsersResponse } from "@/types/admin";
import { UserRow, RoleInvitation, UserStats, UserWithInsights } from "./types";
import { UserRoleInvitationService } from "./user-role-invitation.service";
import { UserListService } from "./user-list.service";
import { UserStatisticsService } from "./user-statistics.service";
import { UserRoleOperationsService } from "./user-role-operations.service";
import { UserStatusOperationsService } from "./user-status-operations.service";
import { logger } from "@/lib/logger";

export type { UserStats, UserWithInsights } from "./types";

export interface IUserService extends IService {
  getAllUsers(
    page?: number,
    limit?: number
  ): Promise<ServiceResponse<UsersResponse>>;
  getUserStats(userId: string): Promise<ServiceResponse<UserStats | undefined>>;
  updateUserRole(userId: string, role: string): Promise<ServiceResponse<void>>;
  toggleUserSuspension(
    userId: string,
    isSuspended: boolean
  ): Promise<ServiceResponse<void>>;
  updateUserStatus(
    userId: string,
    isSuspended: boolean
  ): Promise<ServiceResponse<{ success: boolean }>>;
  assignRole(
    userId: string,
    role: string
  ): Promise<ServiceResponse<{ success: boolean; message?: string }>>;
  removeRole(
    roleId: string,
    userId: string
  ): Promise<ServiceResponse<{ success: boolean; message?: string }>>;
  getUsersWithInsights(
    page?: number,
    limit?: number
  ): Promise<ServiceResponse<UserWithInsights[]>>;
  getUserStatistics(): Promise<
    ServiceResponse<{
      totalUsers: number;
      activeUsers: number;
      suspendedUsers: number;
      newUsersThisMonth: number;
    }>
  >;
  // Role invitation methods
  sendRoleInvitation(params: {
    email: string;
    userId?: string;
    role: string;
    message?: string;
  }): Promise<ServiceResponse<{ invitationId: string; invitationToken: string }>>;
  cancelRoleInvitation(invitationId: string): Promise<ServiceResponse<void>>;
  getRoleInvitations(filters?: {
    status?: string;
    email?: string;
  }): Promise<ServiceResponse<RoleInvitation[]>>;
}

export class UserService implements IUserService {
  readonly name = "user" as const;
  private registry: ServiceRegistry;
  private roleInvitationService: UserRoleInvitationService;
  private listService: UserListService;
  private statisticsService: UserStatisticsService;
  private roleOperationsService: UserRoleOperationsService;
  private statusOperationsService: UserStatusOperationsService;

  constructor(serviceRegistry: ServiceRegistry) {
    this.registry = serviceRegistry;
    this.roleInvitationService = new UserRoleInvitationService(serviceRegistry);
    this.listService = new UserListService(serviceRegistry);
    this.statisticsService = new UserStatisticsService(serviceRegistry);
    this.roleOperationsService = new UserRoleOperationsService(serviceRegistry);
    this.statusOperationsService = new UserStatusOperationsService(serviceRegistry);
  }

  async initialize(): Promise<void> {
    await Promise.all([
      this.roleInvitationService.initialize(),
      this.listService.initialize(),
      this.statisticsService.initialize(),
      this.roleOperationsService.initialize(),
      this.statusOperationsService.initialize(),
    ]);
    logger.info("👥 UserService initialized");
  }

  async dispose(): Promise<void> {
    await Promise.all([
      this.roleInvitationService.dispose(),
      this.listService.dispose(),
      this.statisticsService.dispose(),
      this.roleOperationsService.dispose(),
      this.statusOperationsService.dispose(),
    ]);
    logger.info("👥 UserService disposed");
  }

  async getAllUsers(page = 0, limit = 10): Promise<ServiceResponse<UsersResponse>> {
    try {
      const data = await this.listService.getAllUsers(page, limit);
      return createSuccessResponse(data);
    } catch (error) {
      logger.error("Error in UserService.getAllUsers:", { error: error });
      return createErrorResponse(
        createServiceError("GET_ALL_USERS_FAILED", error instanceof Error ? error.message : "Unknown error occurred", this.name)
      );
    }
  }

  async getUserStats(userId: string): Promise<ServiceResponse<UserStats | undefined>> {
    try {
      const data = await this.statisticsService.getUserStats(userId);
      return createSuccessResponse(data);
    } catch (error) {
      logger.error("Error in UserService.getUserStats:", { error: error });
      return createErrorResponse(
        createServiceError("GET_USER_STATS_FAILED", error instanceof Error ? error.message : "Unknown error occurred", this.name)
      );
    }
  }

  async updateUserRole(userId: string, role: string): Promise<ServiceResponse<void>> {
    try {
      await this.roleOperationsService.updateUserRole(userId, role);
      return createSuccessResponse(undefined);
    } catch (error) {
      logger.error("Error in UserService.updateUserRole:", { error: error });
      return createErrorResponse(
        createServiceError("UPDATE_USER_ROLE_FAILED", error instanceof Error ? error.message : "Unknown error occurred", this.name)
      );
    }
  }

  async toggleUserSuspension(userId: string, isSuspended: boolean): Promise<ServiceResponse<void>> {
    try {
      await this.statusOperationsService.toggleUserSuspension(userId, isSuspended);
      return createSuccessResponse(undefined);
    } catch (error) {
      logger.error("Error in UserService.toggleUserSuspension:", { error: error });
      return createErrorResponse(
        createServiceError("TOGGLE_USER_SUSPENSION_FAILED", error instanceof Error ? error.message : "Unknown error occurred", this.name)
      );
    }
  }

  async updateUserStatus(userId: string, isSuspended: boolean): Promise<ServiceResponse<{ success: boolean }>> {
    try {
      const data = await this.statusOperationsService.updateUserStatus(userId, isSuspended);
      return createSuccessResponse(data);
    } catch (error) {
      logger.error("Error in UserService.updateUserStatus:", { error: error });
      return createErrorResponse(
        createServiceError("UPDATE_USER_STATUS_FAILED", error instanceof Error ? error.message : "Unknown error occurred", this.name)
      );
    }
  }

  async assignRole(userId: string, role: string): Promise<ServiceResponse<{ success: boolean; message?: string }>> {
    try {
      const data = await this.roleOperationsService.assignRole(userId, role);
      return createSuccessResponse(data);
    } catch (error) {
      logger.error("Error in UserService.assignRole:", { error: error });
      return createErrorResponse(
        createServiceError("ASSIGN_ROLE_FAILED", error instanceof Error ? error.message : "Unknown error occurred", this.name)
      );
    }
  }

  async removeRole(roleId: string, userId: string): Promise<ServiceResponse<{ success: boolean; message?: string }>> {
    try {
      const data = await this.roleOperationsService.removeRole(roleId, userId);
      return createSuccessResponse(data);
    } catch (error) {
      logger.error("Error in UserService.removeRole:", { error: error });
      return createErrorResponse(
        createServiceError("REMOVE_ROLE_FAILED", error instanceof Error ? error.message : "Unknown error occurred", this.name)
      );
    }
  }

  async getUsersWithInsights(page = 0, limit = 10): Promise<ServiceResponse<UserWithInsights[]>> {
    try {
      const data = await this.listService.getUsersWithInsights(page, limit);
      return createSuccessResponse(data);
    } catch (error) {
      logger.error("Error in UserService.getUsersWithInsights:", { error: error });
      return createErrorResponse(
        createServiceError("GET_USERS_WITH_INSIGHTS_FAILED", error instanceof Error ? error.message : "Unknown error occurred", this.name)
      );
    }
  }

  async getUserStatistics(): Promise<
    ServiceResponse<{ totalUsers: number; activeUsers: number; suspendedUsers: number; newUsersThisMonth: number }>
  > {
    try {
      const data = await this.statisticsService.getUserStatistics();
      return createSuccessResponse(data);
    } catch (error) {
      logger.error("Error in UserService.getUserStatistics:", { error: error });
      return createErrorResponse(
        createServiceError("GET_USER_STATISTICS_FAILED", error instanceof Error ? error.message : "Unknown error occurred", this.name)
      );
    }
  }

  async sendRoleInvitation(params: {
    email: string;
    userId?: string;
    role: string;
    message?: string;
  }): Promise<ServiceResponse<{ invitationId: string; invitationToken: string }>> {
    return this.roleInvitationService.sendRoleInvitation(params);
  }

  /**
   * Cancel a role invitation
   */
  async cancelRoleInvitation(invitationId: string): Promise<ServiceResponse<void>> {
    return this.roleInvitationService.cancelRoleInvitation(invitationId);
  }

  /**
   * Get role invitations with optional filters
   */
  async getRoleInvitations(filters?: {
    status?: string;
    email?: string;
  }): Promise<ServiceResponse<RoleInvitation[]>> {
    return this.roleInvitationService.getRoleInvitations(filters);
  }
}

// Export singleton instance
// export const userService = new UserService(); // Deprecated singleton

// Helper to wait for service registration with retry logic
const waitForService = async <T extends IService>(name: string, maxRetries = 10, delayMs = 100): Promise<T> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await serviceRegistry.get<T>(name);
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
  throw new Error(`Service '${name}' not available after ${maxRetries} retries`);
};

// ServiceResponseError.error may be a ServiceError object coerced into the string slot by
// createErrorResponse in service-registry.ts. Extract the readable message defensively.
const extractErrorMessage = (err: unknown): string => {
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message);
  return JSON.stringify(err);
};

// Legacy function exports (for backward compatibility during migration)
// These delegate to the registry-managed UserService instance
export const getAllUsers = async (page = 0, limit = 10) => {
  const svc = await waitForService<UserService>("user");
  const res = await svc.getAllUsers(page, limit);
  if (!res.success) throw new Error(extractErrorMessage(res.error));
  return res.data;
};

export const getUserStats = async (userId: string) => {
  const svc = await waitForService<UserService>("user");
  const res = await svc.getUserStats(userId);
  if (!res.success) throw new Error(extractErrorMessage(res.error));
  return res.data;
};

export const updateUserRole = async (userId: string, role: string) => {
  const svc = await waitForService<UserService>("user");
  const res = await svc.updateUserRole(userId, role);
  if (!res.success) throw new Error(extractErrorMessage(res.error));
  return res.data;
};

export const toggleUserSuspension = async (userId: string, isSuspended: boolean) => {
  const svc = await waitForService<UserService>("user");
  const res = await svc.toggleUserSuspension(userId, isSuspended);
  if (!res.success) throw new Error(extractErrorMessage(res.error));
  return res.data;
};

export const updateUserStatus = async (userId: string, isSuspended: boolean) => {
  const svc = await waitForService<UserService>("user");
  const res = await svc.updateUserStatus(userId, isSuspended);
  if (!res.success) throw new Error(extractErrorMessage(res.error));
  return res.data;
};

export const assignRole = async (userId: string, role: string) => {
  const svc = await waitForService<UserService>("user");
  const res = await svc.assignRole(userId, role);
  if (!res.success) throw new Error(extractErrorMessage(res.error));
  return res.data;
};

export const removeRole = async (roleId: string, userId: string) => {
  const svc = await waitForService<UserService>("user");
  const res = await svc.removeRole(roleId, userId);
  if (!res.success) throw new Error(extractErrorMessage(res.error));
  return res.data;
};

export const getUsersWithInsights = async (page = 0, limit = 10) => {
  const svc = await waitForService<UserService>("user");
  const res = await svc.getUsersWithInsights(page, limit);
  if (!res.success) throw new Error(extractErrorMessage(res.error));
  return res.data;
};

export const getUserStatistics = async () => {
  const svc = await waitForService<UserService>("user");
  const res = await svc.getUserStatistics();
  if (!res.success) throw new Error(extractErrorMessage(res.error));
  return res.data;
};
